import apisauce, { CancelToken } from "apisauce"

import { getUserId } from "../hooks/useAttributionTracking"
import { getReferralPromoCode } from "../Utils/referral"

const appType = process.env.REACT_APP_TYPE

// const secret = new fernet.Secret(process.env.REACT_APP_FERNET_SECRET_KEY);
// const generateToken = () => new fernet.Token({ secret: secret, time: Date.parse(new Date()),ttl:120 });

export const encryptedData = (data) => {
  if (!data) return
  try {
    // const endcodedData = generateToken().encode(JSON.stringify(data));
    // return {encrypted: endcodedData};
    // revert for now
    return data
  } catch (error) {
    Promise.reject(error)
    // for cases when stringify fails
    return data
  }
}

export const decryptData = (response) => {
  const isEncodedData = response?.data?.["encrypted"]
  if (isEncodedData) {
    try {
      const decodedDataString = generateToken().decode(isEncodedData)
      response.data = JSON.parse(decodedDataString)
    } catch (error) {
      return Promise.reject(error)
    }
    return response
  }
  return response
}

export const decryptErrorData = (error) => {
  const isEncodedData = error?.response?.data?.["encrypted"]
  if (isEncodedData) {
    try {
      const decodedDataString = generateToken().decode(isEncodedData)
      return JSON.parse(decodedDataString)
    } catch (error) {
      return Promise.reject(error)
    }
  }
  return error
}

export class API {
  constructor() {
    this.baseURL = process.env.REACT_APP_BASE_API_ROUTE

    if (!API.instance) {
      this.create()
      this.createInterceptor()

      API.instance = this
    }

    return API.instance
  }

  create() {
    this.api = apisauce.create({
      // base URL is read from the 'constructor'
      baseURL: this.baseURL,
      // here are some default headers
      headers: {
        "Content-Type": "application/json"
      },
      withCredentials: true,
      cancelToken: new CancelToken((cancel) => {
        this.source = cancel
      }),
      // 60 second timeout...
      timeout: 60000
    })
  }

  cancelAllRequest() {
    API.instance.source()
  }

  createInterceptor() {
    this.interceptors = this.api.axiosInstance.interceptors
    this.api.axiosInstance.interceptors.request.use(
      (config) => {
        const contentType = config.headers?.["Content-Type"] || config.headers?.["content-type"]
        if (contentType && contentType.includes("application/json")) {
          config.data = encryptedData(config?.data)
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )

    this.api.axiosInstance.interceptors.response.use(
      (response) => {
        return decryptData(response)
      },
      (error) => {
        // error.response.data = decryptErrorData(error)
        const decryptedError = decryptErrorData(error)
        return Promise.reject(decryptedError)
      }
    )
  }
}

export const request = new API()

const create = (baseURL = process.env.REACT_APP_BASE_API_ROUTE) => {
  // ------
  // STEP 1
  // ------
  //
  // Create and configure an apisauce-based api object.
  //
  const accessData = { accessToken: null }

  const api = apisauce.create({
    // base URL is read from the 'constructor'
    baseURL,
    // here are some default headers
    headers: {
      "Content-Type": "application/json"
    },
    withCredentials: true,
    // 60 second timeout...
    timeout: 60000
  })

  const apiHandling = async (url, data, method = "post") => {
    return api[method](url, encryptedData(data))
      .then((res) => {
        return decryptData(res)
      })
      .catch((error) => {
        //   error.response.data = decryptErrorData(error)
        //  return Promise.reject(error);
        const decryptedError = decryptErrorData(error)
        return Promise.reject(decryptedError)
      })
  }
  // ------
  // STEP 2
  // ------
  //
  // Define some functions that call the api.  The goal is to provide
  // a thin wrapper of the api layer providing nicer feeling functions
  // rather than 'get', 'post' and friends.
  //
  // I generally don't like wrapping the output at this level because
  // sometimes specific actions need to be take on `403` or `401`, etc.
  //
  // Since we can't hide from that, we embrace it by getting out of the
  // way at this level.
  //

  const updateAccessToken = (accessToken, refreshToken) => {
    accessData.accessToken = accessToken
    accessData.refreshToken = refreshToken

    if (accessToken) {
      api.setHeader("Authorization", `JWT ${accessToken}`)
    } else {
      delete api.headers["Authorization"]
    }
  }

  const saveFrontLogs = (data) => api.post("/frontend_logs/", encryptedData(data))

  const serialize = (obj, prefix) => {
    let str = [],
      p
    for (p in obj) {
      if (obj.hasOwnProperty(p)) {
        let k = prefix ? prefix : p
        let v = obj[p]
        str.push(
          v !== null && typeof v === "object"
            ? serialize(v, k)
            : encodeURIComponent(k) + "=" + encodeURIComponent(v)
        )
      }
    }
    return str.join("&")
  }

  const signIn = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("auth/token/", encryptedData({ ...data, appType: data?.appType || appType }))
        .then((res) => {
          const response = decryptData(res)
          if (response && response.ok && response.data && response.data.access) {
            updateAccessToken(response.data.access, response.data.refresh)
            resolve({
              userInfo: response.data.user,
              access: response.data.access,
              refresh: response.data.refresh
            })
          } else if (!response.ok && response.data) {
            resolve(response)
          } else {
            reject(response)
          }
        })
        .catch((error) => {
          // error.response.data = decryptErrorData(error)
          // reject(error);
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const verify2FA = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("auth/2fa-login/", encryptedData(data))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data?.access) {
            updateAccessToken(response.data.access, response.data.refresh)
            resolve({
              userInfo: response.data.user,
              access: response.data.access,
              refresh: response.data.refresh
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const generate2FASetup = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("auth/2fa-generate/", encryptedData(data))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            resolve(response.data)
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const confirm2FASetup = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("auth/2fa-confirm/", encryptedData(data))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            if (response.data.access) {
              updateAccessToken(response.data.access, response.data.refresh)
            }

            resolve({
              access: response.data.access || null,
              refresh: response.data.refresh || null,
              commitToken: response.data.commit_token || null,
              recoveryCodes: response.data.recovery_codes || []
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const getCurrentUser = () => {
    return new Promise((resolve, reject) => {
      api
        .get("/user/account/")
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            resolve(response.data)
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const generateAdminSecurity2FA = () => {
    return new Promise((resolve, reject) => {
      api
        .post("admin_panel/security/2fa/setup/", encryptedData({}))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            resolve(response.data)
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const confirmAdminSecurity2FA = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("admin_panel/security/2fa/confirm/", encryptedData(data))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            resolve({
              commitToken: response.data.commit_token || null,
              recoveryCodes: response.data.recovery_codes || []
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const commitAdminSecurity2FA = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("auth/2fa-commit/", encryptedData(data))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            if (response.data.access) {
              updateAccessToken(response.data.access, response.data.refresh)
            }

            resolve({
              success: true,
              access: response.data.access || null,
              refresh: response.data.refresh || null
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const acceptAdminInvite = (data) => {
    return new Promise((resolve, reject) => {
      api
        .post("admin_panel/admin-users/accept-invite/", encryptedData(data))
        .then((res) => {
          const response = decryptData(res)

          if (response?.ok && response?.data) {
            resolve({
              ephemeralToken: response.data.ephemeral_token || null
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const resendVerificationLink = (data) => {
    return api.post(`auth/resend-verification-code/`, encryptedData(data))
  }

  const createAccount = ({ data, query, isReferralOutsider }) => {
    if (isReferralOutsider) {
      return apiHandling(`auth/sign-up-referral/`, data)
    }
    return apiHandling(`auth/sign-up/`, data)
  }

  const changeEmail = (data) => apiHandling(`auth/change-email/`, data)

  const sendConfirmEmail = async (email) => {
    try {
      const res = await api.post("auth/confirm-email/", { email })
      return decryptData(res)
    } catch (error) {
      throw decryptErrorData(error)
    }
  }

  const confirmEmail = (hash) => {
    const query = {
      code: hash,
      auth: true
    }
    return new Promise((resolve, reject) => {
      api
        .get("auth/confirm-email/?" + serialize(query))
        .then((res) => {
          const response = decryptData(res)
          if (response && response.ok && response.data && response.data.access) {
            updateAccessToken(response.data.access, response.data.refresh)
            resolve({
              userInfo: response.data.user,
              access: response.data.access,
              refresh: response.data.refresh
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          // error.response.data = decryptErrorData(error)
          // reject(error);
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const confirmEmailInit = (hash) => {
    const query = {
      code: hash
    }
    return new Promise((resolve, reject) => {
      api
        .get("auth/confirm-email/?" + serialize(query))
        .then((res) => {
          const response = decryptData(res)
          if (response && response.ok && response.data && response.data?.code_valid) {
            resolve({
              codeValid: response?.data?.code_valid
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          // error.response.data = decryptErrorData(error)
          // reject(error);
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const checkPassword = (email) =>
    apiHandling(`auth/forgot-password/`, { email, appType: process.env.REACT_APP_TYPE })
  const confirmPassword = (code) => {
    const query = {
      code
    }
    return new Promise((resolve, reject) => {
      api
        .get("auth/forgot-password/?" + serialize(query))
        .then((res) => {
          const response = decryptData(res)
          if (response && response.ok && response.data && response.data.access) {
            updateAccessToken(response.data.access, response.data.refresh)
            resolve({
              userInfo: response.data.user,
              access: response.data.access,
              refresh: response.data.refresh
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          // error.response.data = decryptErrorData(error)
          // reject(error);
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const tokenRefresh = () => {
    return new Promise((resolve, reject) => {
      api
        .post(
          "auth/token/refresh/",
          encryptedData({
            refresh: accessData.refreshToken
          })
        )
        .then((res) => {
          const response = decryptData(res)
          if (response && response.ok && response.data && response.data.access) {
            updateAccessToken(response.data.access, response.data.refresh)
          }
          resolve({
            access: response.data.access,
            refresh: response.data.refresh
          })
        })
        .catch((error) => {
          // error.response.data = decryptErrorData(error)
          // reject(error);
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  //create user password
  const createUserPassword = (new_password, confirm_code) =>
    apiHandling(`/user/set_password/`, { new_password, confirm_code })

  const shopifyAuth = (shop) => apiHandling(`/user/shopify/authorize/?shop=${shop}`, null, "get")

  const socialAuth = (access_token, socialType, user, isReferralOutsider) => {
    const referral_promo_code = getReferralPromoCode()
    if (socialType === "shopify") {
      return new Promise((resolve, reject) => {
        api
          .get(
            `/auth/shopify_auth/`,
            {
              shop: access_token?.["shop"],
              business_account_id: access_token?.["business_account_id"],
              frontend_user_id: getUserId(),
              ...(referral_promo_code && { referral_promo_code })
            },
            access_token.accessToken
              ? { headers: { Authorization: `JWT ${access_token.accessToken}` } }
              : null
          )
          .then((res) => {
            const response = decryptData(res)
            if (response?.ok && response?.data?.access) {
              updateAccessToken(response.data.access, response.data.refresh)
              resolve({
                userInfo: response.data.user,
                access: response.data.access,
                refresh: response.data.refresh
              })
            } else {
              resolve(response)
            }
          })
          .catch((error) => {
            // error.response.data = decryptErrorData(error)
            // reject(error);
            const decryptedError = decryptErrorData(error)
            reject(decryptedError)
          })
      })
    } else if (socialType === "nextcommerce") {
      return new Promise((resolve, reject) => {
        api
          .get(
            `/auth/nextcommerce_auth/`,
            {
              store: access_token?.["store"],
              email: access_token?.["email"],
              business_account_id: access_token?.["business_account_id"],
              frontend_user_id: getUserId(),
              ...(referral_promo_code && { referral_promo_code })
            },
            access_token.accessToken
              ? { headers: { Authorization: `JWT ${access_token.accessToken}` } }
              : null
          )
          .then((res) => {
            const response = decryptData(res)
            if (response?.ok && response?.data?.access) {
              updateAccessToken(response.data.access, response.data.refresh)
              resolve({
                userInfo: response.data.user,
                access: response.data.access,
                refresh: response.data.refresh
              })
            } else {
              resolve(response)
            }
          })
          .catch((error) => {
            // error.response.data = decryptErrorData(error)
            // reject(error);
            const decryptedError = decryptErrorData(error)
            reject(decryptedError)
          })
      })
    } else if (socialType === "stripe") {
      return new Promise((resolve, reject) => {
        api
          .get(
            `/auth/stripe_auth/`,
            {
              account_id: access_token?.["account_id"],
              ...(access_token?.["business_account_id"]
                ? { business_account_id: access_token?.["business_account_id"] }
                : {}),
              frontend_user_id: getUserId(),
              ...(referral_promo_code && { referral_promo_code })
            },
            access_token.accessToken
              ? { headers: { Authorization: `JWT ${access_token.accessToken}` } }
              : null
          )
          .then((res) => {
            const response = decryptData(res)
            if (response?.ok && response?.data?.access) {
              updateAccessToken(response.data.access, response.data.refresh)
              resolve({
                userInfo: response.data.user,
                access: response.data.access,
                refresh: response.data.refresh
              })
            } else {
              resolve(response)
            }
          })
          .catch((error) => {
            // error.response.data = decryptErrorData(error)
            // reject(error);
            const decryptedError = decryptErrorData(error)
            reject(decryptedError)
          })
      })
    } else {
      const frontend_user_id = getUserId()
      return new Promise((resolve, reject) => {
        api
          .post(
            `auth/${appType === "partner" ? "google-referral" : socialType}/`,
            encryptedData({
              access_token,
              appType,
              frontend_user_id,
              ...(referral_promo_code && { referral_promo_code }),
              ...(user && { user: user })
            })
          )
          .then((res) => {
            const response = decryptData(res)
            if (response?.ok && response?.data?.access) {
              updateAccessToken(response.data.access, response.data.refresh)
              resolve({
                userInfo: response.data.user,
                access: response.data.access,
                refresh: response.data.refresh
              })
            } else {
              resolve(response)
            }
          })
          .catch((error) => {
            // error.response.data = decryptErrorData(error)
            // reject(error);
            const decryptedError = decryptErrorData(error)
            reject(decryptedError)
          })
      })
    }
  }

  const socialAuthAdd = (access_token, socialType, user) => {
    return new Promise((resolve, reject) => {
      api
        .post(
          `user/social/${socialType}/`,
          encryptedData({
            access_token,
            ...(user && { user: user })
          })
        )
        .then((res) => {
          const response = decryptData(res)
          if (response?.ok && response?.data?.access) {
            updateAccessToken(response.data.access, response.data.refresh)
            resolve({
              userInfo: response.data.user,
              access: response.data.access,
              refresh: response.data.refresh
            })
          } else {
            resolve(response)
          }
        })
        .catch((error) => {
          // error.response.data = decryptErrorData(error)
          // reject(error);
          const decryptedError = decryptErrorData(error)
          reject(decryptedError)
        })
    })
  }

  const socialAuthDelete = (socialType) => apiHandling(`user/social/${socialType}/`, null, "delete")

  const logout = () => {
    const { accessToken, refreshToken } = accessData
    return api.post("auth/logout/", encryptedData({ refresh_token: refreshToken }), {
      headers: { Authorization: "JWT " + accessToken }
    })
  }

  // ------
  // STEP 3
  // ------
  //
  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  // methods in step 2.
  //
  // Notice we're not returning back the `api` created in step 1?  That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    api,
    // a list of the API functions from step 2
    updateAccessToken,

    saveFrontLogs,
    signIn,
    verify2FA,
    generate2FASetup,
    confirm2FASetup,
    getCurrentUser,
    generateAdminSecurity2FA,
    confirmAdminSecurity2FA,
    commitAdminSecurity2FA,
    acceptAdminInvite,
    logout,
    tokenRefresh,
    resendVerificationLink,
    createAccount,
    changeEmail,
    confirmEmail,
    confirmEmailInit,
    sendConfirmEmail,
    checkPassword,
    confirmPassword,
    createUserPassword,
    shopifyAuth,
    socialAuth,
    socialAuthAdd,
    socialAuthDelete
  }
}

// let's return back our create method as the default.
export default create()
